import { Injectable, inject } from '@angular/core';
import { AuthService } from './auth.service';
import {
  OAUTH_PROVIDERS,
  OAuthProviderId,
  buildRedirectUri,
} from './oauth-providers';

/**
 * Generalized OAuth2 Authorization Code + PKCE client flow.
 *
 *   startLogin(provider)
 *     - generates PKCE verifier + state
 *     - stashes them in sessionStorage
 *     - redirects to the provider's authorize endpoint
 *
 *   completeLogin(provider, query) — called from /auth/callback/:provider
 *     - validates state and TTL
 *     - hands the authorization code + verifier to AuthService.loginUsingOAuth
 *     - returns the originally-requested returnUrl
 *
 * The auth server is responsible for exchanging the code at the provider,
 * verifying the user's identity, and issuing our JWT — see
 * .github/copilot-instructions.md and the OAuth plan for the server contract.
 */

interface PendingOAuth {
  provider: OAuthProviderId;
  verifier: string;
  state: string;
  /** OIDC nonce — present only for providers with useOidcNonce. */
  nonce?: string;
  returnUrl: string;
  createdAt: number;
}

const STORAGE_KEY = 'oauth_pending';
const PENDING_TTL_MS = 10 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class OAuthService {
  private auth = inject(AuthService);

  /**
   * Kick off an OAuth login. Navigates the browser to the provider's authorize
   * endpoint — does not return control to the caller on success.
   */
  async startLogin(providerId: OAuthProviderId, returnUrl: string = '/'): Promise<void> {
    const provider = OAUTH_PROVIDERS[providerId];
    if (!provider.clientId) {
      throw new Error(`${provider.displayName} sign-in is not configured.`);
    }

    const verifier = generateRandomString(64);
    const state = generateRandomString(32);
    const nonce = provider.useOidcNonce ? generateRandomString(32) : undefined;
    const challenge = await pkceChallenge(verifier);

    const pending: PendingOAuth = {
      provider: providerId,
      verifier,
      state,
      nonce,
      returnUrl: safeReturnUrl(returnUrl),
      createdAt: Date.now(),
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: provider.clientId,
      redirect_uri: buildRedirectUri(providerId),
      scope: provider.scope,
      state,
      code_challenge: challenge,
      code_challenge_method: 'S256',
      ...(provider.extraAuthorizeParams ?? {}),
    });
    if (nonce) params.set('nonce', nonce);

    window.location.assign(`${provider.authorizeUrl}?${params.toString()}`);
  }

  /**
   * Finish an OAuth login from the /auth/callback/:provider route.
   * Returns the safe returnUrl to navigate to on success.
   * Throws with a user-facing message on any failure.
   */
  async completeLogin(providerId: OAuthProviderId, query: URLSearchParams): Promise<string> {
    const pending = this.consumePending();

    if (!pending) {
      throw new Error('Login session expired. Please try again.');
    }
    if (Date.now() - pending.createdAt > PENDING_TTL_MS) {
      throw new Error('Login session expired. Please try again.');
    }
    if (pending.provider !== providerId) {
      throw new Error('Login session mismatch. Please try again.');
    }

    const providerError = query.get('error');
    if (providerError) {
      if (providerError === 'access_denied') throw new Error('Login was cancelled.');
      const description = query.get('error_description');
      throw new Error(description ?? `Sign-in failed: ${providerError}`);
    }

    const code = query.get('code');
    const state = query.get('state');
    if (!code || !state) {
      throw new Error('Login response was missing required data.');
    }
    if (state !== pending.state) {
      throw new Error('Login session expired. Please try again.');
    }

    await this.auth.loginUsingOAuth(providerId, code, pending.verifier, pending.nonce);
    return pending.returnUrl;
  }

  /** Read and remove the pending OAuth state in one step — single-use by design. */
  private consumePending(): PendingOAuth | null {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as PendingOAuth;
    } catch {
      return null;
    }
  }
}

/** Cryptographically-random URL-safe string of the given length. */
function generateRandomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, b => chars[b % chars.length]).join('');
}

/** RFC 7636 PKCE challenge: base64url(SHA-256(verifier)). */
async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Same-origin path guard — mirrors login.component.ts to prevent open-redirect. */
function safeReturnUrl(raw: string): string {
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
  if (raw.startsWith('/login') || raw.startsWith('/auth/')) return '/';
  return raw;
}
